/**
 * SignatureDialog Component
 *
 * A complete, reusable signature dialog that combines SignatureCapture and EsignConsent.
 * Provides a ready-to-use modal for any signature requirement across the platform.
 *
 * Features:
 * - Complete signature workflow in a modal
 * - Optional ESIGN Act compliance
 * - Configurable validation and consent items
 * - Audit trail generation
 * - IP address capture support
 *
 * @example
 * ```tsx
 * <SignatureDialog
 *   open={showSignatureDialog}
 *   onOpenChange={setShowSignatureDialog}
 *   title="Sign Document"
 *   description="Please sign to confirm your agreement"
 *   onSignatureComplete={async (data) => {
 *     await saveSignature(data);
 *   }}
 * />
 * ```
 */

import React, { useState, useCallback } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Loader2, Pen } from 'lucide-react';
import { SignatureCapture, type SignatureMetadata } from './SignatureCapture';
import { EsignConsent, type ConsentState, type ConsentItem } from './EsignConsent';
import type { SignatureValidation } from '@/hooks/useSignatureCanvas';

export interface SignatureData {
  dataUrl: string;
  metadata: SignatureMetadata;
  consents?: ConsentState;
  auditLog?: AuditLog;
}

export interface AuditLog {
  timestamp: string;
  ipAddress?: string;
  userAgent: string;
  documentVersion?: string;
  complianceVersion?: string;
  systemVersion?: string;
  additionalData?: Record<string, unknown>;
}

export interface SignatureDialogProps {
  // Dialog control
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Optional class applied to the DialogContent and forwarded to the font-select dropdown (e.g. "lw-ds" to brand-theme this shared dialog). */
  contentClassName?: string;

  // Content
  title?: string;
  description?: string;

  // Signature configuration
  signatureMethods?: ('drawn' | 'typed')[];
  defaultSignatureMethod?: 'drawn' | 'typed';
  signatureValidation?: SignatureValidation;
  initialName?: string;

  // Consent configuration
  requireConsent?: boolean;
  consentItems?: ConsentItem[];
  consentTitle?: string;
  consentDescription?: string;

  // Audit configuration
  captureIpAddress?: boolean;
  ipAddressEndpoint?: string;
  documentVersion?: string;
  additionalAuditData?: Record<string, unknown>;

  // Callbacks
  onSignatureComplete: (data: SignatureData) => void | Promise<void>;
  onCancel?: () => void;

  // Button labels
  submitButtonText?: string;
  cancelButtonText?: string;
}

export const SignatureDialog: React.FC<SignatureDialogProps> = ({
  open,
  onOpenChange,
  contentClassName,
  title = 'Electronic Signature',
  description = 'Please provide your signature to proceed',
  signatureMethods = ['drawn', 'typed'],
  defaultSignatureMethod = 'drawn',
  signatureValidation,
  initialName = '',
  requireConsent = true,
  consentItems,
  consentTitle,
  consentDescription,
  captureIpAddress = false,
  ipAddressEndpoint,
  documentVersion,
  additionalAuditData,
  onSignatureComplete,
  onCancel,
  submitButtonText = 'Sign & Submit',
  cancelButtonText = 'Cancel'
}) => {
  const [signatureData, setSignatureData] = useState<{
    dataUrl: string;
    metadata: SignatureMetadata;
  } | null>(null);
  const [consentState, setConsentState] = useState<ConsentState>({});
  const [allConsentsChecked, setAllConsentsChecked] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Handle signature capture
  const handleSignatureCapture = useCallback((dataUrl: string, metadata: SignatureMetadata) => {
    setSignatureData({ dataUrl, metadata });
  }, []);

  // Handle consent change
  const handleConsentChange = useCallback((consents: ConsentState, allRequired: boolean) => {
    setConsentState(consents);
    setAllConsentsChecked(allRequired);
  }, []);

  // Get IP address if configured
  const getIpAddress = async (): Promise<string> => {
    if (!captureIpAddress || !ipAddressEndpoint) {
      return 'not-captured';
    }

    try {
      const response = await fetch(ipAddressEndpoint);
      const data = await response.json();
      return data.ip || 'unknown';
    } catch (error) {
      console.warn('Failed to capture IP address:', error);
      return 'unknown';
    }
  };

  // Handle form submission
  const handleSubmit = async () => {
    if (!signatureData) {
      // eslint-disable-next-line no-alert -- intentional native guardrail
      alert('Please provide a signature');
      return;
    }

    if (requireConsent && !allConsentsChecked) {
      // eslint-disable-next-line no-alert -- intentional native guardrail
      alert('Please confirm all required consent items');
      return;
    }

    setIsSubmitting(true);

    try {
      // Capture IP address if configured
      const ipAddress = await getIpAddress();

      // Build audit log
      const auditLog: AuditLog = {
        timestamp: new Date().toISOString(),
        ipAddress: captureIpAddress ? ipAddress : undefined,
        userAgent: navigator.userAgent,
        documentVersion,
        complianceVersion: requireConsent ? 'ESIGN-2000' : undefined,
        systemVersion: '1.0.0',
        additionalData: additionalAuditData
      };

      // Prepare complete signature data
      const completeData: SignatureData = {
        dataUrl: signatureData.dataUrl,
        metadata: signatureData.metadata,
        consents: requireConsent ? consentState : undefined,
        auditLog
      };

      // Call parent handler
      await onSignatureComplete(completeData);

      // Close dialog on success
      onOpenChange(false);
    } catch (error) {
      console.error('Failed to submit signature:', error);
      // eslint-disable-next-line no-alert -- intentional native guardrail
      alert('Failed to save signature. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle cancel
  const handleCancel = () => {
    onCancel?.();
    onOpenChange(false);
  };

  // Reset state when dialog opens/closes
  React.useEffect(() => {
    if (!open) {
      setSignatureData(null);
      setConsentState({});
      setAllConsentsChecked(false);
      setIsSubmitting(false);
    }
  }, [open]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className={`sm:max-w-2xl max-h-[90vh] overflow-y-auto${contentClassName ? ` ${contentClassName}` : ''}`}>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          {description && (
            <DialogDescription>{description}</DialogDescription>
          )}
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Signature Capture */}
          <div>
            <h4 className="text-sm font-medium mb-3">Your Signature</h4>
            <SignatureCapture
              methods={signatureMethods}
              defaultMethod={defaultSignatureMethod}
              validation={signatureValidation}
              initialName={initialName}
              onSignatureComplete={handleSignatureCapture}
              showValidationStatus={true}
              selectContentClassName={contentClassName}
            />
          </div>

          {/* ESIGN Consent (if required) */}
          {requireConsent && (
            <div>
              <EsignConsent
                consentItems={consentItems}
                title={consentTitle}
                description={consentDescription}
                onConsentChange={handleConsentChange}
                variant="default"
                showComplianceInfo={true}
              />
            </div>
          )}
        </div>

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={handleCancel}
            disabled={isSubmitting}
          >
            {cancelButtonText}
          </Button>
          <Button
            type="button"
            onClick={handleSubmit}
            disabled={
              !signatureData ||
              (requireConsent && !allConsentsChecked) ||
              isSubmitting
            }
          >
            {isSubmitting ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Submitting...
              </>
            ) : (
              <>
                <Pen className="mr-2 h-4 w-4" />
                {submitButtonText}
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};